import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronRight, ChevronDown, Loader2, Globe, Save, Check } from "lucide-react";
import { getRegions, getCommunesByRegion, updateLogisticsCompany } from "../../../../api/settings";
import { showToast } from "../../../../utils/showToast";
import { Button } from "../../../../components/ui/Button";
import { useCountry } from "../../../../context/CountryContext";
// El pluralizador de rótulos vive en utils: lo comparte el editor de tarifas por comuna,
// que solo ofrece las divisiones cubiertas y necesita nombrarlas igual que esta pestaña.
import { pluralize } from "../../../../utils/divisionLabels";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** "3 regiones" / "1 región" con el rótulo del país, en minúsculas. */
function countLabel(n, singular) {
  const word = n === 1 ? singular : pluralize(singular);
  return `${n} ${word.toLowerCase()}`;
}

function regionCoverage(region, coverageAreas) {
  const area = coverageAreas.find((a) => a.regionId === String(region._id));
  return area?.communeIds ?? [];
}

function countSelected(regions, communesCache, coverageAreas) {
  let totalRegions = 0;
  let totalCommunes = 0;
  regions.forEach((r) => {
    const selected = regionCoverage(r, coverageAreas);
    const allCommunes = communesCache[String(r._id)] ?? [];
    const hasAny = selected.length > 0;
    if (hasAny) totalRegions++;
    totalCommunes += selected.length;
    if (allCommunes.length > 0 && selected.length === allCommunes.length) {
      // counts as full region but communes already counted
    }
  });
  return { totalRegions, totalCommunes };
}

// Indeterminate state: some but not all communes of a region selected
function regionCheckState(regionId, communeIds, coverageAreas) {
  const area = coverageAreas.find((a) => a.regionId === regionId);
  const selected = area?.communeIds ?? [];
  if (selected.length === 0) return "none";
  if (communeIds.length > 0 && selected.length === communeIds.length) return "all";
  return "some";
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton
// ─────────────────────────────────────────────────────────────────────────────

function CoverageSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[1, 2, 3, 4, 5].map((k) => (
        <div key={k} className="as-skeleton" style={{ height: 44, borderRadius: 8 }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RegionRow
// ─────────────────────────────────────────────────────────────────────────────

function RegionRow({ region, coverageAreas, communesCache, onToggleRegion, onToggleCommune, onExpand, expanded, loadingId, filter, labels, t }) {
  const regionId = String(region._id);
  const communes = communesCache[regionId] ?? [];
  const state = regionCheckState(regionId, communes.map((c) => String(c._id)), coverageAreas);
  const checkRef = useRef(null);

  useEffect(() => {
    if (checkRef.current) {
      checkRef.current.indeterminate = state === "some";
    }
  }, [state]);

  // Filter communes
  const filteredCommunes = filter
    ? communes.filter((c) => c.name.toLowerCase().includes(filter))
    : communes;

  const selectedCommunes = regionCoverage(region, coverageAreas);

  // Highlight filter match in region name
  const regionMatches = filter && region.name.toLowerCase().includes(filter);

  return (
    <div className="ct-region">
      {/* Region header */}
      <div className={`ct-region__header${regionMatches ? " ct-region__header--match" : ""}`}>
        <input
          ref={checkRef}
          type="checkbox"
          className="ct-check"
          checked={state === "all"}
          onChange={() => onToggleRegion(region)}
          aria-label={`${t("Seleccionar")} ${labels.region.toLowerCase()} ${region.name}`}
          id={`region-${regionId}`}
        />
        <label htmlFor={`region-${regionId}`} className="ct-region__name">
          {region.name}
          <span className="ct-region__count">
            {selectedCommunes.length > 0
              ? `${selectedCommunes.length}${communes.length ? `/${communes.length}` : ""} ${pluralize(
                  labels.commune,
                ).toLowerCase()}`
              : ""}
          </span>
        </label>
        <button
          type="button"
          className="ct-expand-btn"
          onClick={() => onExpand(regionId)}
          aria-expanded={expanded}
          aria-controls={`communes-${regionId}`}
        >
          {loadingId === regionId
            ? <Loader2 size={14} className="spin" />
            : expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {/* Communes list */}
      {expanded && (
        <ul
          id={`communes-${regionId}`}
          className="ct-communes"
          role="group"
          aria-label={`${pluralize(labels.commune)} ${t("de")} ${region.name}`}
        >
          {loadingId === regionId && communes.length === 0 ? (
            <li className="ct-commune--loading">
              <Loader2 size={13} className="spin" /> {t("Cargando")} {pluralize(labels.commune).toLowerCase()}…
            </li>
          ) : filteredCommunes.length === 0 ? (
            <li className="ct-commune--empty">{t("Sin resultados")}</li>
          ) : (
            filteredCommunes.map((commune) => {
              const communeId = String(commune._id);
              const checked = selectedCommunes.includes(communeId);
              const match = filter && commune.name.toLowerCase().includes(filter);
              return (
                <li key={communeId} className={`ct-commune${match ? " ct-commune--match" : ""}`}>
                  <input
                    type="checkbox"
                    className="ct-check"
                    id={`commune-${communeId}`}
                    checked={checked}
                    onChange={() => onToggleCommune(regionId, communeId)}
                    aria-label={commune.name}
                  />
                  <label htmlFor={`commune-${communeId}`}>{commune.name}</label>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CoverageTab
// ─────────────────────────────────────────────────────────────────────────────

export default function CoverageTab({ company, companyId, onCompanyUpdate }) {
  const { t } = useTranslation();
  // Esta vista ESCRIBE cobertura (ids de región/comuna del país del operador).
  // Por eso no usa el default de Chile: con el país sin resolver muestra un
  // estado vacío, porque guardar ids de otro país corrompería `coverageAreas`.
  const { countryId, resolved, labels, loading: countryLoading } = useCountry();

  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [loadingCommuneId, setLoadingCommuneId] = useState(null);
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [communesCache, setCommunesCache] = useState({});
  const [coverageAreas, setCoverageAreas] = useState([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Init coverage from company data
  useEffect(() => {
    const raw = company?.coverageAreas ?? [];
    setCoverageAreas(
      raw.map((a) => ({
        regionId: String(a.regionId),
        communeIds: (a.communeIds ?? []).map(String),
      }))
    );
    setDirty(false);
  }, [company]);

  // Fetch regions once the operator's country is known. Guard de arranque: sin
  // país resuelto NO se dispara el fetch (ni se cae a Chile).
  useEffect(() => {
    if (countryLoading) return;
    if (!resolved || !countryId) {
      setRegions([]);
      setLoadingRegions(false);
      return;
    }
    setLoadingRegions(true);
    getRegions(countryId)
      .then((res) => {
        const data = res.data ?? res;
        setRegions(Array.isArray(data) ? data : []);
      })
      .catch(() =>
        showToast(t("No se pudieron cargar las {{divisions}}", {
          divisions: pluralize(labels.region).toLowerCase(),
        }), false),
      )
      .finally(() => setLoadingRegions(false));
  }, [countryLoading, resolved, countryId, labels.region, t]);

  const fetchCommunes = useCallback(async (regionId) => {
    if (communesCache[regionId]) return;
    setLoadingCommuneId(regionId);
    try {
      const res = await getCommunesByRegion(regionId);
      const data = res.data ?? res;
      const communes = data.communes ?? [];
      setCommunesCache((prev) => ({ ...prev, [regionId]: communes }));
    } catch {
      showToast(
        t("No se pudieron cargar las {{divisions}}", {
          divisions: pluralize(labels.commune).toLowerCase(),
        }),
        false,
      );
    } finally {
      setLoadingCommuneId(null);
    }
  }, [communesCache, labels.commune, t]);

  const handleExpand = useCallback((regionId) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
        fetchCommunes(regionId);
      }
      return next;
    });
  }, [fetchCommunes]);

  const handleToggleRegion = useCallback((region) => {
    const regionId = String(region._id);
    const communes = communesCache[regionId] ?? [];
    const allCommuneIds = communes.map((c) => String(c._id));
    const existing = coverageAreas.find((a) => a.regionId === regionId);
    const currentSelected = existing?.communeIds ?? [];

    const isFullySelected =
      allCommuneIds.length > 0 && currentSelected.length === allCommuneIds.length;

    let next;
    if (isFullySelected) {
      next = coverageAreas.filter((a) => a.regionId !== regionId);
    } else if (allCommuneIds.length > 0) {
      const area = { regionId, communeIds: allCommuneIds };
      next = existing
        ? coverageAreas.map((a) => (a.regionId === regionId ? area : a))
        : [...coverageAreas, area];
    } else {
      // Communes not loaded yet — expand to load first
      fetchCommunes(regionId);
      handleExpand(regionId);
      return;
    }
    setCoverageAreas(next);
    setDirty(true);
  }, [coverageAreas, communesCache, fetchCommunes, handleExpand]);

  const handleToggleCommune = useCallback((regionId, communeId) => {
    const existing = coverageAreas.find((a) => a.regionId === regionId);
    const selected = existing?.communeIds ?? [];
    const isSelected = selected.includes(communeId);
    const newSelected = isSelected
      ? selected.filter((id) => id !== communeId)
      : [...selected, communeId];

    let next;
    if (newSelected.length === 0) {
      next = coverageAreas.filter((a) => a.regionId !== regionId);
    } else if (existing) {
      next = coverageAreas.map((a) =>
        a.regionId === regionId ? { ...a, communeIds: newSelected } : a
      );
    } else {
      next = [...coverageAreas, { regionId, communeIds: newSelected }];
    }
    setCoverageAreas(next);
    setDirty(true);
  }, [coverageAreas]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateLogisticsCompany(companyId, { coverageAreas });
      onCompanyUpdate(res.data ?? res);
      setDirty(false);
      showToast(t("Cobertura guardada correctamente"), true);
    } catch {
      showToast(t("No se pudo guardar la cobertura"), false);
    } finally {
      setSaving(false);
    }
  };

  const normalizedFilter = filter.trim().toLowerCase();

  const filteredRegions = normalizedFilter
    ? regions.filter(
        (r) =>
          r.name.toLowerCase().includes(normalizedFilter) ||
          (communesCache[String(r._id)] ?? []).some((c) =>
            c.name.toLowerCase().includes(normalizedFilter)
          )
      )
    : regions;

  const { totalRegions, totalCommunes } = countSelected(regions, communesCache, coverageAreas);

  // Rótulos derivados del país (Región/Comuna en Chile, Provincia/Municipio en
  // Argentina). Se calculan una vez para no repetir el pluralizador en el JSX.
  const regionWord = labels.region.toLowerCase();
  const communeWord = labels.commune.toLowerCase();
  const searchLabel = t("Buscar {{region}} o {{commune}}", {
    region: regionWord,
    commune: communeWord,
  });

  // Empresa sin país de operación: no se muestra el árbol. Caer a Chile acá
  // escribiría cobertura con ids de otro país (el guardado persiste ObjectIds).
  if (!countryLoading && !resolved) {
    return (
      <div className="w-full min-w-0 ct-root">
        <div className="as-empty">
          <Globe size={36} className="as-empty__icon" />
          <h3 className="as-empty__title">{t("Falta el país de operación")}</h3>
          <p className="as-empty__body">
            {t(
              "Tu empresa no tiene un país de operación configurado, así que no se puede definir la cobertura. Contacta a soporte para completarlo.",
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 ct-root">
      {/* ── Summary bar ──────────────────────────────────────────── */}
      <div className="ct-summary">
        <div className="ct-summary__counts">
          <span className="as-badge as-badge--success">
            <Globe size={12} />
            {countLabel(totalRegions, labels.region)}
          </span>
          <span className="as-badge as-badge--warning">
            {countLabel(totalCommunes, labels.commune)}
          </span>
        </div>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !dirty}
          loading={saving}
          iconLeft={dirty ? <Save size={16} /> : <Check size={16} />}
          aria-label={t("Guardar cobertura")}
        >
          {saving ? t("Guardando…") : dirty ? t("Guardar cobertura") : t("Sin cambios")}
        </Button>
      </div>

      {/* ── Search ───────────────────────────────────────────────── */}
      <div className="ct-search">
        <Search size={15} className="ct-search__icon" aria-hidden="true" />
        <input
          type="search"
          className="ct-search__input"
          placeholder={`${searchLabel}…`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label={searchLabel}
        />
      </div>

      {/* ── Tree ─────────────────────────────────────────────────── */}
      {loadingRegions ? (
        <CoverageSkeleton />
      ) : regions.length === 0 ? (
        // País resuelto pero sin catálogo: el mensaje de "sin resultados de
        // búsqueda" confundiría (no hay nada que buscar).
        <div className="as-empty">
          <Globe size={36} className="as-empty__icon" />
          <h3 className="as-empty__title">
            {t("Sin {{divisions}} disponibles", { divisions: pluralize(labels.region).toLowerCase() })}
          </h3>
          <p className="as-empty__body">
            {t("No se pudo cargar el catálogo geográfico de tu país de operación. Reintenta más tarde o contacta a soporte.")}
          </p>
        </div>
      ) : filteredRegions.length === 0 ? (
        <div className="as-empty">
          <Globe size={36} className="as-empty__icon" />
          <h3 className="as-empty__title">{t("Sin resultados")}</h3>
          <p className="as-empty__body">
            {t("No se encontraron {{regions}} o {{communes}} que coincidan con tu búsqueda.", {
              regions: pluralize(labels.region).toLowerCase(),
              communes: pluralize(labels.commune).toLowerCase(),
            })}
          </p>
        </div>
      ) : (
        <div
          className="ct-tree"
          role="tree"
          aria-label={t("Árbol de cobertura por {{region}}", { region: regionWord })}
        >
          {filteredRegions.map((region) => (
            <RegionRow
              key={String(region._id)}
              region={region}
              coverageAreas={coverageAreas}
              communesCache={communesCache}
              onToggleRegion={handleToggleRegion}
              onToggleCommune={handleToggleCommune}
              onExpand={handleExpand}
              expanded={expandedRegions.has(String(region._id))}
              loadingId={loadingCommuneId}
              filter={normalizedFilter}
              labels={labels}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}
